import React, { useState, useEffect } from 'react';
import { 
  UserPlusIcon, 
  FunnelIcon, 
  MagnifyingGlassIcon,
  PlusIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserGroupIcon,
  PencilIcon,
  TrashIcon,
  ArrowRightIcon,
  ChatBubbleLeftIcon,
  CodeBracketIcon,
  ArrowDownTrayIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { leadService } from '../services/leadService';
import { Lead, LeadStatus } from '../types/lead';
import CommonPageHeader from '../components/common/CommonPageHeader';
import LeadCard from '../components/leads/LeadCard';
import LeadFormModal from '../components/leads/LeadFormModal';
import LeadConversionModal from '../components/leads/LeadConversionModal';
import LeadCommentsModal from '../components/leads/LeadCommentsModal';
import LeadEmbedModal from '../components/leads/LeadEmbedModal';
import ConfirmDeleteModal from '../components/common/ConfirmDeleteModal';
import toast from 'react-hot-toast';

const Leads: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<LeadStatus | 'all'>('all');
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isConversionModalOpen, setIsConversionModalOpen] = useState(false);
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [showConverted, setShowConverted] = useState(false);
  const [followUpFilter, setFollowUpFilter] = useState<'all' | 'overdue' | 'today' | 'tomorrow' | 'thisweek'>('all');
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState<string | null>(null);
  const [isEmbedModalOpen, setIsEmbedModalOpen] = useState(false);

  useEffect(() => {
    if (user?.organizationId) {
      loadLeads();
    }
  }, [user?.organizationId]);

  useEffect(() => {
    filterLeads();
  }, [leads, searchTerm, statusFilter, showConverted, followUpFilter]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedLeadIds([]);
  }, [searchTerm, statusFilter, showConverted, itemsPerPage, followUpFilter]);

  const loadLeads = async () => {
    if (!user?.organizationId) return;
    
    try {
      setLoading(true);
      const data = await leadService.getLeads(user.organizationId);
      setLeads(data);
    } catch (error) {
      console.error('Error loading leads:', error);
      toast.error(t('leads.messages.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const filterLeads = () => {
    let filtered = [...leads];

    if (!showConverted && statusFilter !== 'converted') {
      filtered = filtered.filter(lead => lead.status !== 'converted');
    }

    // Státusz szűrés
    if (statusFilter !== 'all') {
      filtered = filtered.filter(lead => lead.status === statusFilter);
    }

    // Emlékeztető szűrés
    if (followUpFilter !== 'all') {
      filtered = filtered.filter(lead => {
        if (!lead.nextFollowUpAt) return false;
        const followUpDate = lead.nextFollowUpAt instanceof Date
          ? lead.nextFollowUpAt
          : new Date(lead.nextFollowUpAt);
        const now = new Date();
        const diffMs = followUpDate.getTime() - now.getTime();
        const diffHours = diffMs / (1000 * 60 * 60);

        if (followUpFilter === 'overdue') return diffMs < 0;
        if (followUpFilter === 'today') return diffMs >= 0 && diffHours <= 24;
        if (followUpFilter === 'tomorrow') return diffHours > 24 && diffHours <= 48;
        if (followUpFilter === 'thisweek') return diffHours > 48 && diffHours <= 168;

        return false;
      });
    }

    // Keresés
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(lead => 
        lead.name.toLowerCase().includes(term) ||
        lead.email?.toLowerCase().includes(term) ||
        lead.company?.toLowerCase().includes(term) ||
        lead.phone?.toLowerCase().includes(term)
      );
    }

    setFilteredLeads(filtered);
  };

  const handleCreateLead = () => {
    setSelectedLead(null);
    setIsFormModalOpen(true);
  };

  const handleEditLead = (lead: Lead) => {
    setSelectedLead(lead);
    setIsFormModalOpen(true);
  };

  const handleConvertLead = (lead: Lead) => {
    setSelectedLead(lead);
    setIsConversionModalOpen(true);
  };

  const handleDeleteLead = (leadId: string) => {
    setLeadToDelete(leadId);
    setIsDeleteConfirmOpen(true);
  };

  const confirmDeleteLead = async () => {
    if (!leadToDelete) return;

    try {
      await leadService.deleteLead(leadToDelete);
      toast.success(t('leads.messages.deleted'));
      loadLeads();
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast.error(t('leads.messages.errorDeleting'));
    } finally {
      setLeadToDelete(null);
    }
  };

  const handleStatusChange = async (leadId: string, status: LeadStatus) => {
    try {
      await leadService.updateLeadStatus(leadId, status, user?.id);
      toast.success(t('leads.messages.statusUpdated'));
      loadLeads();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(t('leads.messages.errorStatus'));
    }
  };

  const getPaginatedLeads = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredLeads.slice(startIndex, endIndex);
  };

  const handleSelectLead = (leadId: string) => {
    setSelectedLeadIds(prev =>
      prev.includes(leadId)
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    const paginated = getPaginatedLeads();
    if (selectedLeadIds.length === paginated.length) {
      setSelectedLeadIds([]);
    } else {
      setSelectedLeadIds(paginated.map(lead => lead.id));
    }
  };

  const handleBulkStatusChange = async (newStatus: LeadStatus) => {
    if (selectedLeadIds.length === 0) {
      toast.error('Nincs kiválasztott lead');
      return;
    }
    try {
      const promises = selectedLeadIds.map(leadId =>
        leadService.updateLeadStatus(leadId, newStatus, user?.id)
      );
      await Promise.all(promises);
      toast.success(`${selectedLeadIds.length} lead státusza frissítve`);
      setSelectedLeadIds([]);
      loadLeads();
    } catch (error) {
      console.error('Error bulk updating leads:', error);
      toast.error('Hiba a lead-ek frissítésekor');
    }
  };

  const handleExportCSV = () => {
    const headers = ['Név', 'Email', 'Telefon', 'Cég', 'Státusz', 'Forrás', 'Létrehozva'];
    const rows = filteredLeads.map(lead => [
      lead.name,
      lead.email || '',
      lead.phone || '',
      lead.company || '',
      lead.status,
      lead.source,
      new Date(lead.createdAt).toLocaleDateString('hu-HU')
    ]);
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `leads_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Lead-ek CSV-be exportálva');
  };

  const handleViewComments = (lead: Lead) => {
    setSelectedLead(lead);
    setIsCommentsModalOpen(true);
  };

  const getStatusIcon = (status: LeadStatus) => {
    switch (status) {
      case 'new':
        return <ClockIcon className="h-5 w-5 text-blue-500" />;
      case 'contacted':
        return <UserGroupIcon className="h-5 w-5 text-yellow-500" />;
      case 'qualified':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'unqualified':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'converted':
        return <CheckCircleIcon className="h-5 w-5 text-purple-500" />;
      default:
        return null;
    }
  };

  const getStatusCount = (status: LeadStatus | 'all') => {
    if (status === 'all') return leads.length;
    return leads.filter(lead => lead.status === status).length;
  };

  const statusOptions: { value: LeadStatus | 'all'; label: string; color: string }[] = [
    { value: 'all', label: t('leads.status.all'), color: 'gray' },
    { value: 'new', label: t('leads.status.new'), color: 'blue' },
    { value: 'contacted', label: t('leads.status.contacted'), color: 'yellow' },
    { value: 'qualified', label: t('leads.status.qualified'), color: 'green' },
    { value: 'unqualified', label: t('leads.status.unqualified'), color: 'red' },
    { value: 'converted', label: t('leads.status.converted'), color: 'purple' },
  ];

  // Lapozó: csak releváns oldalszámok + ...
  const getVisiblePages = (current: number, total: number): (number | string)[] => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: (number | string)[] = [1];
    if (current > 4) pages.push('...');
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (current < total - 3) pages.push('...');
    pages.push(total);
    return pages;
  };

  // Pagination computed values
  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLeads = getPaginatedLeads();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <CommonPageHeader
        title={t('leads.title')}
        subtitle={t('leads.subtitle')}
        icon={UserPlusIcon}
        actionButton={
          <button
            onClick={handleCreateLead}
            className="inline-flex items-center px-5 py-2.5 bg-white text-primary-600 rounded-lg hover:bg-white/90 transition-all shadow-lg hover:shadow-xl font-semibold"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            {t('leads.actions.newLead')}
          </button>
        }
      >
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={loadLeads}
            className="inline-flex items-center px-3 sm:px-4 py-2 sm:py-2.5 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-all border border-white/20 font-medium text-sm"
            title="Frissítés"
          >
            <ArrowPathIcon className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2" />
            <span className="hidden sm:inline">Frissítés</span>
          </button>
          <button
            onClick={handleExportCSV}
            className="inline-flex items-center px-3 sm:px-4 py-2 sm:py-2.5 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-all border border-white/20 font-medium text-sm"
            title="Export CSV"
          >
            <ArrowDownTrayIcon className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2" />
            <span className="hidden sm:inline">Export</span>
          </button>
          <button
            onClick={() => setIsEmbedModalOpen(true)}
            className="inline-flex items-center px-3 sm:px-4 py-2 sm:py-2.5 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-all border border-white/20 font-medium text-sm"
          >
            <CodeBracketIcon className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2" />
            <span className="hidden sm:inline">{t('leads.actions.embed')}</span>
            <span className="sm:hidden">{t('leads.actions.embedShort')}</span>
          </button>
        </div>
      </CommonPageHeader>

      <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('leads.search.placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-5 w-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as LeadStatus | 'all')}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label} ({getStatusCount(option.value)})
                  </option>
                ))}
              </select>
            </div>

            {/* Follow-up Filter */}
            <div className="flex items-center space-x-2">
              <CalendarIcon className="h-5 w-5 text-gray-400" />
              <select
                value={followUpFilter}
                onChange={(e) => setFollowUpFilter(e.target.value as any)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="all">Összes emlékeztető</option>
                <option value="overdue">Lejárt</option>
                <option value="today">Ma</option>
                <option value="tomorrow">Holnap</option>
                <option value="thisweek">Ezen a héten</option>
              </select>
            </div>

            {/* Show converted toggle */}
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showConverted}
                onChange={(e) => setShowConverted(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Konvertáltak mutatása</span>
            </label>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {statusOptions.filter(s => s.value !== 'all').map(status => (
            <button
              key={status.value}
              onClick={() => setStatusFilter(status.value)}
              className={`p-4 rounded-lg border-2 transition-all ${
                statusFilter === status.value
                  ? `border-${status.color}-500 bg-${status.color}-50 dark:bg-${status.color}-900/20`
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {status.label}
                </span>
                {getStatusIcon(status.value as LeadStatus)}
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {getStatusCount(status.value as LeadStatus)}
              </div>
            </button>
          ))}
        </div>

        {/* Leads Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
            <UserPlusIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Nincs megjeleníthető lead
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              {searchTerm || statusFilter !== 'all' 
                ? 'Próbálj meg más szűrőket használni'
                : 'Kezdj el lead-eket hozzáadni vagy szinkronizálj Google Sheets-ből'
              }
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <button
                onClick={handleCreateLead}
                className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Első Lead létrehozása
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Bulk actions toolbar */}
            {selectedLeadIds.length > 0 && (
              <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700 rounded-lg p-3 mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                    {selectedLeadIds.length} lead kiválasztva
                  </span>
                  <button
                    onClick={() => setSelectedLeadIds([])}
                    className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Kijelölés megszüntetése
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Státusz:</span>
                  {(['new', 'contacted', 'qualified', 'unqualified'] as LeadStatus[]).map(status => (
                    <button
                      key={status}
                      onClick={() => handleBulkStatusChange(status)}
                      className={`text-xs px-2 py-1 rounded-md font-medium ${
                        status === 'new' ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' :
                        status === 'contacted' ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' :
                        status === 'qualified' ? 'bg-green-100 text-green-700 hover:bg-green-200' :
                        'bg-red-100 text-red-700 hover:bg-red-200'
                      }`}
                    >
                      {status === 'new' ? 'Új' :
                       status === 'contacted' ? 'Kapcsolatba lépve' :
                       status === 'qualified' ? 'Minősített' :
                       'Nem minősített'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Mobile/Tablet view - Cards */}
            <div className="2xl:hidden grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {paginatedLeads.map(lead => (
                <div key={lead.id} className="relative">
                  <div className="absolute top-2 left-2 z-10">
                    <input
                      type="checkbox"
                      checked={selectedLeadIds.includes(lead.id)}
                      onChange={() => handleSelectLead(lead.id)}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </div>
                  <LeadCard
                    lead={lead}
                    onEdit={handleEditLead}
                    onDelete={handleDeleteLead}
                    onConvert={handleConvertLead}
                    onStatusChange={handleStatusChange}
                    onViewComments={handleViewComments}
                  />
                </div>
              ))}
            </div>

            {/* Desktop view - Table (1536px+) */}
            <div className="hidden 2xl:block bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={paginatedLeads.length > 0 && selectedLeadIds.length === paginatedLeads.length}
                        onChange={handleSelectAll}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('leads.columns.name')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('leads.columns.contact')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('leads.columns.company')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('leads.columns.status')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('leads.columns.source')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('leads.columns.created')}</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('leads.columns.actions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedLeads.map(lead => (
                    <tr 
                      key={lead.id} 
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      onClick={() => handleEditLead(lead)}
                    >
                      <td className="px-4 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedLeadIds.includes(lead.id)}
                          onChange={() => handleSelectLead(lead.id)}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{lead.name}</div>
                        {lead.notes && <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">{lead.notes}</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {lead.email ? (
                          <a 
                            href={`mailto:${lead.email}`}
                            className="text-sm font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:underline transition-all"
                          >
                            {lead.email}
                          </a>
                        ) : (
                          <span className="text-sm text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {lead.phone ? (
                          <a 
                            href={`tel:${lead.phone}`}
                            className="text-sm font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:underline transition-all"
                          >
                            {lead.phone}
                          </a>
                        ) : (
                          <span className="text-sm text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">{lead.company || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            lead.status === 'new' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                            lead.status === 'contacted' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                            lead.status === 'qualified' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                            lead.status === 'unqualified' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                            'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                          }`}>
                            {lead.status === 'new' ? 'Új' :
                             lead.status === 'contacted' ? 'Kapcsolatba lépve' :
                             lead.status === 'qualified' ? 'Minősített' :
                             lead.status === 'unqualified' ? 'Nem minősített' :
                             'Konvertálva'}
                          </span>
                          {lead.status === 'converted' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleStatusChange(lead.id, 'qualified'); }}
                              className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white underline"
                              title="Visszaállítás minősített státuszra"
                            >
                              Visszaállítás
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {t(`leads.source.${lead.source}`)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {new Date(lead.createdAt).toLocaleDateString('hu-HU')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleViewComments(lead); }}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                            title={t('leads.actions.viewComments')}
                          >
                            <ChatBubbleLeftIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEditLead(lead); }}
                            className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                            title={t('leads.actions.edit')}
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          {lead.status !== 'converted' && (
                            <>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleConvertLead(lead); }}
                                className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                                title={t('leads.actions.convert')}
                              >
                                <ArrowRightIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDeleteLead(lead.id); }}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                title={t('leads.actions.delete')}
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('leads.pagination.previous')}
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('leads.pagination.next')}
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    {t('leads.pagination.showing')} <span className="font-medium">{startIndex + 1}</span> - <span className="font-medium">{Math.min(endIndex, filteredLeads.length)}</span> {t('leads.pagination.of')} <span className="font-medium">{filteredLeads.length}</span> {t('leads.pagination.results')}
                  </p>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 dark:bg-gray-700 dark:text-white"
                  >
                    <option value={20}>20 / oldal</option>
                    <option value={50}>50 / oldal</option>
                    <option value={100}>100 / oldal</option>
                  </select>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ‹
                    </button>
                    {getVisiblePages(currentPage, totalPages).map((page, idx) => (
                      page === '...' ? (
                        <span
                          key={`ellipsis-${idx}`}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-400 dark:text-gray-500"
                        >
                          ...
                        </span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page as number)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === page
                              ? 'z-10 bg-primary-50 dark:bg-primary-900/20 border-primary-500 text-primary-600 dark:text-primary-400'
                              : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    ))}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ›
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
          </>
        )}
      </div>

      {/* Modals */}
      {isFormModalOpen && (
        <LeadFormModal
          lead={selectedLead}
          onClose={() => {
            setIsFormModalOpen(false);
            setSelectedLead(null);
          }}
          onSuccess={() => {
            setIsFormModalOpen(false);
            setSelectedLead(null);
            loadLeads();
          }}
        />
      )}

      {isConversionModalOpen && selectedLead && (
        <LeadConversionModal
          lead={selectedLead}
          onClose={() => {
            setIsConversionModalOpen(false);
            setSelectedLead(null);
          }}
          onSuccess={() => {
            setIsConversionModalOpen(false);
            setSelectedLead(null);
            loadLeads();
          }}
        />
      )}

      {selectedLead && (
        <LeadCommentsModal
          lead={selectedLead}
          isOpen={isCommentsModalOpen}
          onClose={() => {
            setIsCommentsModalOpen(false);
            setSelectedLead(null);
          }}
        />
      )}

      <ConfirmDeleteModal
        isOpen={isDeleteConfirmOpen}
        onClose={() => {
          setIsDeleteConfirmOpen(false);
          setLeadToDelete(null);
        }}
        onConfirm={confirmDeleteLead}
        title={t('leads.actions.delete')}
        deleteMessage={t('leads.messages.deleteConfirm')}
        deleteWarning={t('leads.messages.deleteWarning')}
      />

      <LeadEmbedModal
        isOpen={isEmbedModalOpen}
        onClose={() => setIsEmbedModalOpen(false)}
      />
    </div>
  );
};

export default Leads;
